import {
    AlertDialog,
    AlertDialogBody,
    AlertDialogContent,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogOverlay,
    Box,
    Button,
    Drawer,
    DrawerBody,
    DrawerCloseButton,
    DrawerContent,
    DrawerHeader,
    DrawerOverlay,
    Flex,
    IconButton,
    Text,
    useDisclosure,
} from '@chakra-ui/react';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { GlobalContext } from 'providers/Global';
import { ModalType, SCREEN_MD } from '../../utils/constants';
import { AddressBookEntry } from 'types/addressBook';
import { getAddressBook, deleteAddressBookEntry } from 'api/addressBookAPI';
import { useToast } from 'hooks/useToast';
import AddressBookModal from './AddressBookModal';
import { ReactComponent as DeleteIcon } from 'assets/delete-icon.svg';
import { ReactComponent as CopyIcon } from 'assets/copy-icon.svg';
import { ReactComponent as EditIcon } from 'assets/edit-icon.svg';
import copyToClipboard from 'copy-to-clipboard';

const AddressBookDrawer = () => {
    const {
        connection: { accountId },
        modals: {
            [`${ModalType.AddressBookDrawer}`]: { open },
        },
        closeModal,
    } = useContext(GlobalContext);

    const isMobile = useMediaQuery({ query: `(max-width: ${SCREEN_MD})` });
    const { showToast } = useToast();
    const { isOpen: isModalOpen, onOpen: onModalOpen, onClose: onModalClose } = useDisclosure();

    const [entries, setEntries] = useState<AddressBookEntry[]>([]);
    const [editEntry, setEditEntry] = useState<AddressBookEntry | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [deleteId, setDeleteId] = useState<string | null>(null);
    const cancelDeleteRef = useRef<HTMLButtonElement>(null);
    const limit = 10;

    const fetchEntries = useCallback(async () => {
        if (!accountId) return;
        setIsLoading(true);
        try {
            const response = await getAddressBook(page, limit);
            if (response?.data?.entries) {
                setEntries(response.data.entries);
                setTotalPages(response.data.totalPages || 1);
            } else {
                setEntries([]);
            }
        } catch (error: unknown) {
            console.error('Failed to fetch address book:', error);
            setEntries([]);
        } finally {
            setIsLoading(false);
        }
    }, [accountId, page]);

    // Reset page when drawer opens
    useEffect(() => {
        if (open) setPage(1);
    }, [open]);

    // Fetch when page or accountId changes (while open)
    useEffect(() => {
        if (open) fetchEntries();
    }, [open, fetchEntries]);

    const handleAdd = () => {
        setEditEntry(null);
        onModalOpen();
    };

    const handleEdit = (entry: AddressBookEntry) => {
        setEditEntry(entry);
        onModalOpen();
    };

    const confirmDelete = async () => {
        if (!deleteId) return;
        try {
            const result = await deleteAddressBookEntry(deleteId);
            if (!result) throw new Error('Delete request failed');
            showToast('Contact deleted', 'success', undefined, 'top');
            fetchEntries();
        } catch (error: unknown) {
            console.error('Failed to delete entry:', error);
            showToast('Failed to delete contact', 'error', undefined, 'top');
        } finally {
            setDeleteId(null);
        }
    };

    return (
        <>
            <Drawer
                isOpen={open}
                onClose={() => closeModal(ModalType.AddressBookDrawer)}
                placement="right"
                size={isMobile ? 'full' : 'lg'}
            >
                <DrawerOverlay backdropFilter="blur(10px)" />
                <DrawerContent>
                    <DrawerCloseButton />
                    <DrawerHeader borderBottomWidth="1px">
                        <Flex justifyContent="space-between" alignItems="center" pr={8}>
                            <Text variant="bodyBold" fontSize="xl">
                                Address Book
                            </Text>
                            <Button
                                size="xs"
                                variant="primary"
                                borderRadius={6}
                                onClick={handleAdd}
                            >
                                Add Contact
                            </Button>
                        </Flex>
                    </DrawerHeader>
                    <DrawerBody p={0}>
                        {isLoading ? (
                            <Flex justify="center" align="center" py={16}>
                                <Text size="sm" color="blackAlpha.500">Loading contacts...</Text>
                            </Flex>
                        ) : entries.length === 0 ? (
                            <Flex
                                direction="column"
                                align="center"
                                justify="center"
                                py={20}
                                px={4}
                                textAlign="center"
                            >
                                <Text size="lg" variant="bodyBold" color="blackAlpha.800" mb={1}>
                                    No contacts yet
                                </Text>
                                <Text size="sm" color="blackAlpha.600" mb={6}>
                                    Your address book is empty
                                </Text>
                                <Button
                                    variant="primary"
                                    borderRadius={6}
                                    onClick={handleAdd}
                                    size="md"
                                >
                                    Add Contact
                                </Button>
                            </Flex>
                        ) : (
                            <Flex direction="column">
                                {entries.map((entry, index) => (
                                    <Flex
                                        key={entry._id || index}
                                        gap={3}
                                        px={4}
                                        py={3}
                                        alignItems="center"
                                        borderBottomWidth="1px"
                                        borderColor="blackAlpha.100"
                                        _hover={{ bg: 'blackAlpha.50' }}
                                    >
                                        <Box flex={1} minW={0}>
                                            <Text size="sm" variant="bodyBold" isTruncated>
                                                {entry.name}
                                            </Text>
                                            <Flex alignItems="center" gap={1}>
                                                <Text fontSize="xs" color="blackAlpha.600" isTruncated>
                                                    {entry.address}
                                                </Text>
                                                <Box
                                                    as="button"
                                                    flexShrink={0}
                                                    onClick={() => {
                                                        copyToClipboard(entry.address);
                                                        showToast('Copied to clipboard', 'success', undefined, 'top');
                                                    }}
                                                >
                                                    <CopyIcon width={14} height={14} />
                                                </Box>
                                            </Flex>
                                            {entry.notes && (
                                                <Text fontSize="xs" color="blackAlpha.400" mt={0.5} isTruncated>
                                                    {entry.notes}
                                                </Text>
                                            )}
                                        </Box>
                                        <Flex gap={1} flexShrink={0}>
                                            <IconButton
                                                aria-label="Edit contact"
                                                size="xs"
                                                variant="ghost"
                                                onClick={() => handleEdit(entry)}
                                                icon={<EditIcon width={14} height={14} />}
                                                h={8}
                                            />
                                            <IconButton
                                                aria-label="Delete contact"
                                                size="xs"
                                                variant="ghost"
                                                icon={<DeleteIcon width={14} height={14} />}
                                                onClick={() => setDeleteId(entry._id)}
                                                h={8}
                                            />
                                        </Flex>
                                    </Flex>
                                ))}

                                {totalPages > 1 && (
                                    <Flex justify="center" align="center" py={4} gap={4}>
                                        <Button
                                            size="sm"
                                            variant="primary"
                                            onClick={() => setPage(p => Math.max(1, p - 1))}
                                            isDisabled={page === 1}
                                        >
                                            Previous
                                        </Button>
                                        <Text size="xs">Page {page} of {totalPages}</Text>
                                        <Button
                                            size="sm"
                                            variant="primary"
                                            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                            isDisabled={page === totalPages}
                                        >
                                            Next
                                        </Button>
                                    </Flex>
                                )}
                            </Flex>
                        )}
                    </DrawerBody>
                </DrawerContent>
            </Drawer>

            <AddressBookModal
                isOpen={isModalOpen}
                onClose={onModalClose}
                onSave={fetchEntries}
                entry={editEntry}
                userAccountId={accountId}
            />

            <AlertDialog
                isOpen={!!deleteId}
                leastDestructiveRef={cancelDeleteRef}
                onClose={() => setDeleteId(null)}
                isCentered
            >
                <AlertDialogOverlay>
                    <AlertDialogContent borderRadius={16} mx={4}>
                        <AlertDialogHeader fontSize="lg" fontWeight="bold">
                            Delete Contact
                        </AlertDialogHeader>
                        <AlertDialogBody>
                            Are you sure you want to delete this contact? This action cannot be undone.
                        </AlertDialogBody>
                        <AlertDialogFooter gap={3}>
                            <Button
                                ref={cancelDeleteRef}
                                onClick={() => setDeleteId(null)}
                                borderRadius={6}
                                size="sm"
                            >
                                Cancel
                            </Button>
                            <Button
                                colorScheme="red"
                                onClick={confirmDelete}
                                borderRadius={6}
                                size="sm"
                            >
                                Delete
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </>
    );
};

export default AddressBookDrawer;
